import React, { useState, useRef, useEffect } from 'react';
import './InteractiveViewer.css';

function InteractiveViewer({ 
  topic, 
  onZoom, 
  onPan, 
  onHotspotClick, 
  onToggleBloodFlow,
  onStartCardiacCycle,
  onPauseCardiacCycle,
  onSelectCardiacPhase,
  onToggleLayer,
  isTeacher,
  zoomLevel: externalZoom,
  panX: externalPanX,
  panY: externalPanY,
  activeHotspot: externalHotspot,
  bloodFlowEnabled: externalBloodFlow,
  cardiacCyclePlaying: externalCardiacCycle,
  activeCardiacPhase: externalCardiacPhase,
  visibleLayers: externalLayers
}) {
  const [zoom, setZoom] = useState(externalZoom || 1);
  const [pan, setPan] = useState({ x: externalPanX || 0, y: externalPanY || 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedPart, setSelectedPart] = useState(null);
  const [bloodFlowEnabled, setBloodFlowEnabled] = useState(externalBloodFlow || false);
  const [cardiacCyclePlaying, setCardiacCyclePlaying] = useState(externalCardiacCycle || false);
  const [activeCardiacPhase, setActiveCardiacPhase] = useState(externalCardiacPhase || null);
  const [visibleLayers, setVisibleLayers] = useState(externalLayers || {
    chambers: true,
    valves: true,
    vessels: true,
    bloodFlow: false,
    labels: true,
    conduction: true
  });
  const [bloodFlowAnimation, setBloodFlowAnimation] = useState(0);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Sync with external state for display panel
  useEffect(() => {
    if (!isTeacher) {
      if (externalZoom !== undefined) {
        setZoom(externalZoom);
        setPan({ x: externalPanX || 0, y: externalPanY || 0 });
      }
      if (externalBloodFlow !== undefined) {
        setBloodFlowEnabled(externalBloodFlow);
      }
      if (externalCardiacCycle !== undefined) {
        setCardiacCyclePlaying(externalCardiacCycle);
      }
      if (externalCardiacPhase !== undefined) {
        setActiveCardiacPhase(externalCardiacPhase);
      }
      if (externalLayers) {
        setVisibleLayers(externalLayers);
      }
    }
  }, [externalZoom, externalPanX, externalPanY, externalBloodFlow, externalCardiacCycle, externalCardiacPhase, externalLayers, isTeacher]);

  // Blood flow animation - runs when blood flow is enabled
  useEffect(() => {
    if (bloodFlowEnabled && visibleLayers.bloodFlow) {
      const animate = () => {
        setBloodFlowAnimation(prev => (prev + 0.02) % 1);
        animationFrameRef.current = requestAnimationFrame(animate);
      };
      animationFrameRef.current = requestAnimationFrame(animate);
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    } else {
      // Reset animation when disabled
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
  }, [bloodFlowEnabled, visibleLayers.bloodFlow]);

  const handleWheel = (e) => {
    if (!isTeacher) return;
    
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(0.5, Math.min(5, zoom + delta));
    setZoom(newZoom);
    
    if (onZoom) {
      onZoom(newZoom, pan.x, pan.y);
    }
  };

  const handleMouseDown = (e) => {
    if (!isTeacher) return;
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - pan.x,
      y: e.clientY - pan.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isTeacher || !isDragging) return;
    
    const newPan = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };
    setPan(newPan);
    
    if (onPan) {
      onPan(newPan.x, newPan.y);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (!isTeacher) return;
    
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - pan.x,
        y: e.touches[0].clientY - pan.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (!isTeacher || !isDragging) return;
    
    if (e.touches.length === 1) {
      const newPan = {
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      };
      setPan(newPan);
      
      if (onPan) {
        onPan(newPan.x, newPan.y);
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handlePartClick = (part) => {
    setSelectedPart(part);
    if (isTeacher && onHotspotClick) {
      onHotspotClick(part);
    }
  };

  const handleToggleBloodFlow = () => {
    const newState = !bloodFlowEnabled;
    setBloodFlowEnabled(newState);
    if (newState) {
      setVisibleLayers(prev => ({ ...prev, bloodFlow: true }));
    }
    if (onToggleBloodFlow) {
      onToggleBloodFlow(newState);
    }
  };

  const handleStartCardiacCycle = () => {
    setCardiacCyclePlaying(true);
    if (onStartCardiacCycle) {
      onStartCardiacCycle();
    }
  };

  const handlePauseCardiacCycle = () => {
    setCardiacCyclePlaying(false);
    if (onPauseCardiacCycle) {
      onPauseCardiacCycle();
    }
  };

  const handleSelectCardiacPhase = (phase) => {
    setActiveCardiacPhase(phase.id);
    setCardiacCyclePlaying(false);
    if (onSelectCardiacPhase) {
      onSelectCardiacPhase(phase.id);
    }
  };

  const handleToggleLayer = (layerName) => {
    const newState = !visibleLayers[layerName];
    setVisibleLayers(prev => ({ ...prev, [layerName]: newState }));
    if (onToggleLayer) {
      onToggleLayer(layerName, newState);
    }
  };

  const parts = topic?.content?.parts || [];
  const bloodFlow = topic?.content?.bloodFlow;
  const cardiacCycle = topic?.content?.cardiacCycle;

  // Render anatomically accurate heart diagram (NCERT style)
  const renderHeartDiagram = () => {
    return (
      <svg 
        viewBox="0 0 600 700" 
        className="heart-diagram"
        width="600"
        height="700"
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', backgroundColor: '#f8f9fa' }}
      >
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="5" refY="5" orient="auto">
            <polygon points="0,0 10,5 0,10" fill="#fbbf24" />
          </marker>
        </defs>
        
        {/* Background rectangle for visibility */}
        <rect width="600" height="700" fill="#f8f9fa" />
        
        {/* Background - Heart muscle outline - anatomically accurate shape */}
        <path
          d="M 300 40 
             L 250 50 
             L 200 70 
             L 160 100 
             L 140 140 
             L 130 180 
             L 135 220 
             L 150 260 
             L 180 300 
             L 220 340 
             L 270 380 
             L 300 400 
             L 330 380 
             L 380 340 
             L 420 300 
             L 450 260 
             L 465 220 
             L 470 180 
             L 460 140 
             L 440 100 
             L 400 70 
             L 350 50 
             L 300 40 Z"
          fill="#d4a574"
          stroke="#8b6f47"
          strokeWidth="4"
          opacity={visibleLayers.chambers ? 1 : 0.3}
        />
        
        {/* Interventricular Septum - divides left and right ventricles */}
        {visibleLayers.chambers && (
          <line
            x1="300"
            y1="140"
            x2="300"
            y2="380"
            stroke="#8b6f47"
            strokeWidth="4"
            opacity="0.6"
          />
        )}

        {/* Blood Vessels - Deoxygenated (Blue) */}
        {visibleLayers.vessels && (
          <>
            {/* Superior Vena Cava */}
            <rect
              x="180"
              y="50"
              width="40"
              height="40"
              fill="#1e40af"
              stroke="#1e3a8a"
              strokeWidth="2"
            />
            <text x="200" y="75" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">Superior Vena Cava</text>
            
            {/* Inferior Vena Cava */}
            <rect
              x="180"
              y="410"
              width="40"
              height="40"
              fill="#1e40af"
              stroke="#1e3a8a"
              strokeWidth="2"
            />
            <text x="200" y="435" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">Inferior Vena Cava</text>
            
            {/* Pulmonary Artery (deoxygenated blood) - curved vessel */}
            <path
              d="M 190 280 
                 Q 150 260 120 240 
                 Q 100 220 95 200 
                 Q 100 180 120 170 
                 Q 140 160 160 170 
                 Q 180 180 190 200 
                 Q 200 220 195 240 
                 Q 190 260 190 280 Z"
              fill="#1e40af"
              stroke="#1e3a8a"
              strokeWidth="2"
            />
            <text x="140" y="220" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">Pulmonary Artery</text>
          </>
        )}

        {/* Blood Vessels - Oxygenated (Red) */}
        {visibleLayers.vessels && (
          <>
            {/* Aorta - curved vessel */}
            <path
              d="M 390 50 
                 Q 420 40 450 60 
                 Q 480 80 490 110 
                 Q 500 140 480 170 
                 Q 460 200 420 210 
                 Q 400 200 390 180 
                 Q 380 160 385 140 
                 Q 390 120 400 100 
                 Q 410 80 400 70 
                 Q 395 60 390 50 Z"
              fill="#dc2626"
              stroke="#991b1b"
              strokeWidth="2"
            />
            <text x="450" y="100" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">Aorta</text>
            
            {/* Pulmonary Veins (oxygenated blood) - curved vessel */}
            <path
              d="M 410 280 
                 Q 450 260 480 240 
                 Q 500 220 505 200 
                 Q 500 180 480 170 
                 Q 460 160 440 170 
                 Q 420 180 410 200 
                 Q 400 220 405 240 
                 Q 410 260 410 280 Z"
              fill="#dc2626"
              stroke="#991b1b"
              strokeWidth="2"
            />
            <text x="460" y="220" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">Pulmonary Veins</text>
          </>
        )}

        {/* Chambers - Right Side (Deoxygenated - Blue) */}
        {visibleLayers.chambers && (
          <>
            {/* Right Atrium - upper right chamber */}
            <path
              d="M 200 80 
                 L 200 120 
                 Q 200 140 220 140 
                 Q 240 140 260 140 
                 Q 280 140 300 140 
                 L 300 120 
                 L 300 100 
                 Q 280 100 260 95 
                 Q 240 90 220 90 
                 Q 200 90 200 80 Z"
              fill="#3b82f6"
              stroke="#1e40af"
              strokeWidth="2"
              opacity="0.8"
            />
            <text x="250" y="115" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="bold">Right Atrium</text>
            
            {/* Right Ventricle - lower right chamber */}
            <path
              d="M 200 140 
                 L 200 320 
                 Q 200 340 220 340 
                 Q 240 340 260 340 
                 Q 280 340 300 340 
                 L 300 320 
                 L 300 200 
                 Q 280 200 260 195 
                 Q 240 190 220 190 
                 Q 200 190 200 140 Z"
              fill="#3b82f6"
              stroke="#1e40af"
              strokeWidth="2"
              opacity="0.8"
            />
            <text x="250" y="250" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="bold">Right Ventricle</text>
          </>
        )}

        {/* Chambers - Left Side (Oxygenated - Red) */}
        {visibleLayers.chambers && (
          <>
            {/* Left Atrium - upper left chamber */}
            <path
              d="M 300 80 
                 L 300 120 
                 L 300 140 
                 Q 320 140 340 140 
                 Q 360 140 380 140 
                 Q 400 140 400 120 
                 L 400 100 
                 Q 380 100 360 95 
                 Q 340 90 320 90 
                 Q 300 90 300 80 Z"
              fill="#ef4444"
              stroke="#dc2626"
              strokeWidth="2"
              opacity="0.8"
            />
            <text x="350" y="115" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="bold">Left Atrium</text>
            
            {/* Left Ventricle - lower left chamber */}
            <path
              d="M 300 140 
                 L 300 200 
                 L 300 320 
                 L 300 340 
                 Q 320 340 340 340 
                 Q 360 340 380 340 
                 Q 400 340 400 320 
                 L 400 200 
                 Q 380 200 360 195 
                 Q 340 190 320 190 
                 Q 300 190 300 140 Z"
              fill="#ef4444"
              stroke="#dc2626"
              strokeWidth="2"
              opacity="0.8"
            />
            <text x="350" y="250" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="bold">Left Ventricle</text>
          </>
        )}

        {/* Valves */}
        {visibleLayers.valves && (
          <>
            {/* Tricuspid Valve - between RA and RV */}
            <ellipse cx="240" cy="200" rx="20" ry="10" fill="#fff" stroke="#1971c2" strokeWidth="2" />
            <text x="240" y="205" textAnchor="middle" fill="#000" fontSize="10" fontWeight="bold">Tricuspid</text>
            
            {/* Mitral Valve (Bicuspid) - between LA and LV */}
            <ellipse cx="360" cy="200" rx="20" ry="10" fill="#fff" stroke="#1971c2" strokeWidth="2" />
            <text x="360" y="205" textAnchor="middle" fill="#000" fontSize="10" fontWeight="bold">Mitral</text>
            
            {/* Aortic Valve - between LV and Aorta */}
            <ellipse cx="400" cy="140" rx="15" ry="12" fill="#fff" stroke="#1971c2" strokeWidth="2" />
            <text x="400" y="145" textAnchor="middle" fill="#000" fontSize="10" fontWeight="bold">Aortic</text>
            
            {/* Pulmonary Valve - between RV and Pulmonary Artery */}
            <ellipse cx="200" cy="240" rx="15" ry="12" fill="#fff" stroke="#1971c2" strokeWidth="2" />
            <text x="200" y="245" textAnchor="middle" fill="#000" fontSize="10" fontWeight="bold">Pulmonary</text>
          </>
        )}

        {/* Electrical Conduction System (Yellow) */}
        {visibleLayers.conduction && (
          <>
            {/* SA Node (Sino-atrial node) - in Right Atrium */}
            <ellipse cx="220" cy="110" rx="10" ry="8" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" />
            <text x="220" y="113" textAnchor="middle" fill="#000" fontSize="9" fontWeight="bold">SA Node</text>
            
            {/* AV Node (Atrio-ventricular node) - between atria and ventricles */}
            <ellipse cx="260" cy="180" rx="10" ry="8" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" />
            <text x="260" y="183" textAnchor="middle" fill="#000" fontSize="9" fontWeight="bold">AV Node</text>
            
            {/* Bundle of His - down the septum */}
            <line
              x1="300"
              y1="200"
              x2="300"
              y2="360"
              stroke="#fbbf24"
              strokeWidth="5"
              markerEnd="url(#arrowhead)"
            />
            <text x="320" y="280" textAnchor="start" fill="#000" fontSize="10" fontWeight="bold">Bundle of His</text>
          </>
        )}

        {/* Chordae Tendinae - support structures for valves */}
        {visibleLayers.valves && (
          <>
            {/* Chordae tendinae for Tricuspid Valve */}
            <line x1="240" y1="200" x2="230" y2="230" stroke="#fff" strokeWidth="2" opacity="0.7" />
            <line x1="240" y1="200" x2="250" y2="230" stroke="#fff" strokeWidth="2" opacity="0.7" />
            <text x="240" y="260" textAnchor="middle" fill="#fff" fontSize="9">Chordae Tendinae</text>
          </>
        )}

        {/* Apex - bottom tip of heart */}
        {visibleLayers.chambers && (
          <>
            <circle cx="300" cy="400" r="6" fill="#8b6f47" />
            <text x="300" y="420" textAnchor="middle" fill="#000" fontSize="12" fontWeight="bold">Apex</text>
          </>
        )}

        {/* Animated Blood Flow */}
        {bloodFlowEnabled && visibleLayers.bloodFlow && bloodFlow && bloodFlow.paths.map((path, pathIndex) => {
          const pathProgress = (bloodFlowAnimation + pathIndex * 0.3) % 1;
          const totalSegments = path.route.length - 1;
          const currentSegment = Math.floor(pathProgress * totalSegments);
          const segmentProgress = (pathProgress * totalSegments) % 1;
          
          return (
            <g key={path.id} className="blood-flow-path">
              {/* Draw all path segments */}
              {path.route.map((point, pointIndex) => {
                if (pointIndex === 0) return null;
                const prevPoint = path.route[pointIndex - 1];
                const startX = (prevPoint.x / 100) * 600;
                const startY = (prevPoint.y / 100) * 700;
                const endX = (point.x / 100) * 600;
                const endY = (point.y / 100) * 700;
                
                return (
                  <line
                    key={`line-${path.id}-${pointIndex}`}
                    x1={startX}
                    y1={startY}
                    x2={endX}
                    y2={endY}
                    stroke={path.color}
                    strokeWidth="8"
                    opacity="0.5"
                    strokeDasharray="12,6"
                  />
                );
              })}
              
              {/* Animated blood cell */}
              {currentSegment < totalSegments && (() => {
                const startPoint = path.route[currentSegment];
                const endPoint = path.route[currentSegment + 1];
                const startX = (startPoint.x / 100) * 600;
                const startY = (startPoint.y / 100) * 700;
                const endX = (endPoint.x / 100) * 600;
                const endY = (endPoint.y / 100) * 700;
                const currentX = startX + (endX - startX) * segmentProgress;
                const currentY = startY + (endY - startY) * segmentProgress;
                
                return (
                  <circle
                    key={`cell-${path.id}`}
                    cx={currentX}
                    cy={currentY}
                    r="12"
                    fill={path.color}
                    opacity="0.9"
                  >
                    <animate
                      attributeName="r"
                      values="10;14;10"
                      dur="0.6s"
                      repeatCount="indefinite"
                    />
                  </circle>
                );
              })()}
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div className="interactive-viewer-wrapper">
      <div
        className="interactive-viewer"
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: isTeacher && isDragging ? 'grabbing' : isTeacher ? 'grab' : 'default' }}
      >
        <div
          className="viewer-content"
          ref={contentRef}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center'
          }}
        >
          {topic?.content?.type === 'interactive-heart' && (
            <div className="interactive-content">
              {renderHeartDiagram()}
              
              {/* Interactive part hotspots */}
              {parts.map((part) => (
                <div
                  key={part.id}
                  className={`part-hotspot ${selectedPart?.id === part.id ? 'active' : ''}`}
                  style={{
                    left: `${part.position.x}%`,
                    top: `${part.position.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  onClick={() => handlePartClick(part)}
                  title={part.name}
                >
                  <div className="hotspot-marker"></div>
                  {visibleLayers.labels && isTeacher && (
                    <div className="hotspot-label">{part.name}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {isTeacher && (
          <>
            <div className="zoom-controls">
              <button onClick={() => {
                const newZoom = Math.min(5, zoom + 0.2);
                setZoom(newZoom);
                if (onZoom) onZoom(newZoom, pan.x, pan.y);
              }}>
                +
              </button>
              <span>{Math.round(zoom * 100)}%</span>
              <button onClick={() => {
                const newZoom = Math.max(0.5, zoom - 0.2);
                setZoom(newZoom);
                if (onZoom) onZoom(newZoom, pan.x, pan.y);
              }}>
                −
              </button>
            </div>

            {/* Interactive Controls Panel */}
            <div className="controls-panel">
              <div className="control-section">
                <h4>Layers</h4>
                <label>
                  <input
                    type="checkbox"
                    checked={visibleLayers.chambers}
                    onChange={() => handleToggleLayer('chambers')}
                  />
                  Chambers
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={visibleLayers.valves}
                    onChange={() => handleToggleLayer('valves')}
                  />
                  Valves
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={visibleLayers.vessels}
                    onChange={() => handleToggleLayer('vessels')}
                  />
                  Vessels
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={visibleLayers.labels}
                    onChange={() => handleToggleLayer('labels')}
                  />
                  Labels
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={visibleLayers.conduction}
                    onChange={() => handleToggleLayer('conduction')}
                  />
                  Electrical Conduction
                </label>
              </div>

              {bloodFlow && (
                <div className="control-section">
                  <h4>Blood Flow</h4>
                  <button
                    className={bloodFlowEnabled ? 'active' : ''}
                    onClick={handleToggleBloodFlow}
                  >
                    {bloodFlowEnabled ? 'Hide' : 'Show'} Blood Flow
                  </button>
                  <label>
                    <input
                      type="checkbox"
                      checked={visibleLayers.bloodFlow}
                      onChange={() => handleToggleLayer('bloodFlow')}
                    />
                    Show Flow Paths
                  </label>
                </div>
              )}

              {cardiacCycle && (
                <div className="control-section">
                  <h4>Cardiac Cycle</h4>
                  <button
                    onClick={cardiacCyclePlaying ? handlePauseCardiacCycle : handleStartCardiacCycle}
                  >
                    {cardiacCyclePlaying ? 'Pause' : 'Play'}
                  </button>
                  <div className="cardiac-phases">
                    {cardiacCycle.phases.map(phase => (
                      <button
                        key={phase.id}
                        className={activeCardiacPhase === phase.id ? 'active' : ''}
                        onClick={() => handleSelectCardiacPhase(phase)}
                      >
                        {phase.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Information Panel */}
      {selectedPart && (
        <div className="info-panel">
          <button className="close-btn" onClick={() => setSelectedPart(null)}>×</button>
          <h3>{selectedPart.name}</h3>
          <p className="description">{selectedPart.description}</p>
          <div className="details">
            <h4>Details:</h4>
            <p>{selectedPart.details}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default InteractiveViewer;
